import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export const HeroSection = ({ scrollToBooking }) => {
  const heroImageRef = useRef(null);
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const node = heroImageRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHeroVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <div className="absolute inset-0 bg-gradient-to-br from-[#F5F1E8] via-[#E8DCC8] to-[#D4C4A8] opacity-60"></div>

      <div className="absolute top-20 left-10 w-32 h-32 border-t-2 border-l-2 border-[#C9A961] opacity-30"></div>
      <div className="absolute bottom-20 right-10 w-32 h-32 border-b-2 border-r-2 border-[#C9A961] opacity-30"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
        <h1 className="text-7xl md:text-9xl font-serif text-[#8B7355] mb-4 leading-tight">
          Shot to the Nines
        </h1>

        <p className="text-2xl md:text-3xl text-[#C9A961] mb-6 font-serif italic">
          Handcrafted Vintage-Style Booth
        </p>

        <p className="text-3xl md:text-4xl text-[#8B7355] mb-8 max-w-4xl mx-auto font-serif leading-relaxed">
          Something your guests will <strong className="font-bold">actually</strong> remember
        </p>

        <div className="mb-8">
          <Badge variant="outline" className="border-[#C9A961] text-[#8B7355] px-4 py-2 text-sm font-serif bg-white/50 backdrop-blur-sm">
            Est. 2026 • Brisbane & Gold Coast
          </Badge>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <Button
            onClick={scrollToBooking}
            className="bg-[#8B7355] hover:bg-[#6B5845] text-white px-8 py-6 text-lg rounded-none font-serif shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            data-testid="hero-book-btn"
          >
            Book Your Event
          </Button>
          <Button
            variant="outline"
            onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
            className="border-2 border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white px-8 py-6 text-lg rounded-none font-serif transition-all duration-300"
            data-testid="hero-view-packages-btn"
          >
            View Packages
          </Button>
        </div>

        <div
          ref={heroImageRef}
          className={`relative mt-20 group transition-all duration-[1400ms] ease-out will-change-transform ${
            heroVisible
              ? 'opacity-100 translate-y-0 blur-0 scale-100'
              : 'opacity-0 translate-y-12 blur-md scale-[0.96]'
          }`}
          data-testid="hero-nameplate-container"
        >
          <div className="absolute -inset-8 bg-gradient-to-br from-[#C9A961]/40 via-[#8B7355]/20 to-[#C9A961]/30 blur-3xl rounded-full opacity-70 group-hover:opacity-90 transition-opacity duration-700"></div>
          <div className="absolute inset-0 translate-x-3 translate-y-3 bg-[#8B7355]/15 rounded-lg"></div>
          <div className="relative rounded-lg overflow-hidden ring-1 ring-[#8B7355]/20 shadow-[0_30px_80px_-20px_rgba(139,115,85,0.55)] transform transition-transform duration-700 group-hover:-translate-y-1">
            <img
              src="/booth8.jpg"
              alt="Shot to the Nines - Handcrafted Brass Nameplate"
              className="w-full h-auto object-cover"
              data-testid="hero-nameplate-image"
            />
            <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_120px_rgba(0,0,0,0.35)]"></div>
          </div>
        </div>
      </div>
    </section>
  );
};
